import React, { useState, useEffect, Suspense, lazy } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import TournamentRules from './components/TournamentRules';
import About from './components/About';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, collection } from 'firebase/firestore';
import './App.css';

// Lazy Load components to prevent circular dependencies and initialization errors
const LoginForm = lazy(() => import('./components/LoginForm'));
const RegistrationForm = lazy(() => import('./components/RegistrationForm'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const RequestsPage = lazy(() => import('./components/RequestsPage'));
const HistoryPage = lazy(() => import('./components/HistoryPage'));
const ReportPage = lazy(() => import('./components/ReportPage'));
const PlayersPage = lazy(() => import('./components/PlayersPage'));
const ManageFees = lazy(() => import('./components/ManageFees'));
const Policies = lazy(() => import('./components/Policies'));
const ParaApp = lazy(() => import('./para/ParaApp'));

export const formatDate = (dateStr) => {
  if (!dateStr || !dateStr.includes('-')) return dateStr;
  try {
    const [year, month, day] = dateStr.split('-');
    const date = new Date(year, month - 1, day);
    const dayNum = parseInt(day);
    const suffix = (dayNum % 10 === 1 && dayNum !== 11) ? 'st' :
      (dayNum % 10 === 2 && dayNum !== 12) ? 'nd' :
        (dayNum % 10 === 3 && dayNum !== 13) ? 'rd' : 'th';
    const monthName = date.toLocaleString('default', { month: 'long' });
    return `${dayNum}${suffix} ${monthName} ${year}`;
  } catch (e) {
    return dateStr;
  }
};

// Hidden login entry point for the main admin panel (no public LOGIN button): /admin
const isAdminRoute = () =>
  typeof window !== 'undefined' &&
  window.location.pathname.toLowerCase().replace(/\/+$/, '') === '/admin';

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(
    isAdminRoute() ? 'login' : (localStorage.getItem('ttab_view') || 'home')
  );
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [policySection, setPolicySection] = useState(null);

  // Para route detection
  const [isPara, setIsPara] = useState(() => {
    return typeof window !== 'undefined' && window.location.pathname.toLowerCase().startsWith('/para');
  });

  useEffect(() => {
    const handlePopState = () => {
      setIsPara(window.location.pathname.toLowerCase().startsWith('/para'));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Para event open/closed listener (Section 5)
  const [paraEventOpen, setParaEventOpen] = useState(false);
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'paraEvent'), (docSnap) => {
      if (docSnap.exists()) {
        setParaEventOpen(docSnap.data().isOpen === true);
      } else {
        setParaEventOpen(false);
      }
    }, (err) => {
      console.warn("Could not read settings/paraEvent flag:", err);
    });
    return () => unsub();
  }, []);

  // Reset scroll on page change
  useEffect(() => {
    window.scrollTo(0, 0);
    localStorage.setItem('ttab_view', currentPage);
  }, [currentPage]);

  // Tournament Settings State
  const [tournamentTitle, setTournamentTitle] = useState('1st OPEN BARODA MAJOR RANKING TABLE TENNIS TOURNAMENT');
  const [tournamentYear, setTournamentYear] = useState(new Date().getFullYear());
  const [tournamentDates, setTournamentDates] = useState('');
  const [tournamentVenue, setTournamentVenue] = useState('');
  const [lastDateEntry, setLastDateEntry] = useState('');
  const [lastDateWithdrawal, setLastDateWithdrawal] = useState('');
  const [heroDescription, setHeroDescription] = useState('Experience the thrill of elite table tennis in Baroda. Join our upcoming tournaments, enhance your skills, and be part of a legacy of champions.');
  const [registrationEnabled, setRegistrationEnabled] = useState(null);
  const [prices, setPrices] = useState({
    REGISTRATION: 200,
    ms_open: 400, ws_open: 400,
    jb19: 400, jb17: 400, sbj15: 300, cb13: 300, hb11: 300, hb09: 0,
    jg19: 400, jg17: 400, sjg15: 300, cg13: 300, hg11: 300, hg09: 0,
    vm39: 400, vm49: 400, vm59: 400, vm64: 400,
    vw39: 400, vw49: 400, vw59: 400, vw64: 400,
    md: 500, mx: 500
  });
  const [homeBgImage, setHomeBgImage] = useState('');
  const [eventNames, setEventNames] = useState({});
  const [eventVisibility, setEventVisibility] = useState({});
  const [eventVisibilityLoaded, setEventVisibilityLoaded] = useState(false);
  const [users, setUsers] = useState(null);
  const [resetRequests, setResetRequests] = useState([]);

  // Auth Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in — listen to their Firestore profile
        const unsubProfile = onSnapshot(
          doc(db, 'users', user.uid),
          (docSnap) => {
            if (docSnap.exists()) {
              const userData = docSnap.data();
              // Forced Admin Rule: If email is admin@ttab.com, role is ALWAYS admin.
              if (user.email === 'admin@ttab.com') {
                userData.role = 'admin';
              }

              setCurrentUser({ uid: user.uid, ...userData });

              // Allow users to browse 'home' even when logged in. 
              // We only redirect during initial login or if they try to access login/register while already logged in.
              if (userData.role !== 'admin') {
                setCurrentPage(prev =>
                  (prev === 'login' || prev === 'register')
                    ? 'dashboard'
                    : prev
                );
              }
            } else if (user.email === 'admin@ttab.com') {
              setCurrentUser({ uid: user.uid, email: user.email, role: 'admin' });
              // No redirect for admin even if doc doesn't exist
            } else {
              // Document doesn't exist yet — this is NORMAL during registration.
              // The user is mid-flow (Auth created, profile not yet written).
              // Do NOT sign out or throw an error — just wait silently.
              setCurrentUser(null);
            }
            setLoading(false);
          },
          (error) => {
            // Silently handle permission-denied — this is expected when the
            // profile document doesn't exist yet (between Auth creation and
            // the Firestore write at the end of registration).
            setLoading(false);
          }
        );
        return () => unsubProfile();
      } else {
        setCurrentUser(null);
        setLoading(false);
        setCurrentPage(isAdminRoute() ? 'login' : 'home');
        localStorage.removeItem('ttab_view');
      }
    });
    return () => unsubscribe();
  }, []); // Run only once on mount


  // Event Visibility Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'event_visibility'), (doc) => {
      if (doc.exists()) {
        setEventVisibility(doc.data());
      } else {
        setEventVisibility({});
      }
      setEventVisibilityLoaded(true);
    });
    return () => unsubscribe();
  }, []);

  // Event Names Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'event_names'), (doc) => {
      if (doc.exists()) {
        setEventNames(doc.data());
      }
    });
    return () => unsubscribe();
  }, []);

  // Pricing Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'pricing'), (doc) => {
      if (doc.exists()) {
        setPrices(prev => ({ ...prev, ...doc.data() }));
      }
    });
    return () => unsubscribe();
  }, []);
  // Hero BG Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'hero'), (doc) => {
      if (doc.exists()) {
        setHomeBgImage(doc.data().imageUrl || '');
      }
    });
    return () => unsubscribe();
  }, []);

  // Tournament Info Listener
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'tournament'), (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (data.title) setTournamentTitle(data.title);
        if (data.year) setTournamentYear(data.year);
        setTournamentDates(data.dates || '');
        setTournamentVenue(data.venue || '');
        const lastEntryText = data.lastDateEntry || '';
        setLastDateEntry(lastEntryText);
        setLastDateWithdrawal(data.lastDateWithdrawal || '');
        setHeroDescription(data.heroDescription || '');

        // SMART AUTO-CLOSE LOGIC:
        // Try to read a date from the text "19th May 2026"
        const manualEnabled = data.registrationEnabled !== false;
        let isPastDeadline = false;

        if (lastEntryText) {
          // Clean the text: remove "st", "nd", "rd", "th" and common extra words
          const cleanDateText = lastEntryText
            .replace(/(\d+)(st|nd|rd|th)/gi, '$1') // Change "19th" to "19"
            .replace(/before.*/gi, '')              // Remove "before 7:30 PM"
            .trim();

          const deadlineDate = new Date(cleanDateText);

          // Check if it's a valid date
          if (!isNaN(deadlineDate.getTime())) {
            deadlineDate.setHours(23, 59, 59, 999); // Set to end of the day
            const now = new Date();
            if (now > deadlineDate) {
              isPastDeadline = true;
            }
          }
        }

        setRegistrationEnabled(manualEnabled && !isPastDeadline);
      }
    });
    return () => unsubscribe();
  }, []);

  // Admin: Password Reset Requests Listener
  useEffect(() => {
    if (currentUser?.role === 'admin') {
      const unsubscribe = onSnapshot(collection(db, 'password_reset_requests'), (snapshot) => {
        const requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setResetRequests(requests.filter(r => r.status === 'pending'));
      });
      return () => unsubscribe();
    }
  }, [currentUser]);

  // Admin: Users List Listener
  useEffect(() => {
    if (currentUser?.role === 'admin') {
      const unsubscribe = onSnapshot(collection(db, 'users'),
        (snapshot) => {
          const usersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setUsers(usersList);
        },
        (error) => {
          console.error("Firestore Admin Listener Error:", error);
          setUsers([]); // Prevent infinite loading
        }
      );
      return () => unsubscribe();
    }
  }, [currentUser]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLoginSuccess = () => setCurrentPage('dashboard');
  const handleRegisterSuccess = () => setCurrentPage('dashboard');
  const handleUpdateUser = (newData) => setCurrentUser(prev => ({ ...prev, ...newData }));

  const handleLogout = async () => {
    try {
      await auth.signOut();
      setCurrentUser(null);
      setCurrentPage('home');
    } catch (err) {
      console.error("Logout error:", err);
      // Fallback: clear state anyway
      setCurrentUser(null);
      setCurrentPage('home');
    }
  };

  const renderContent = () => {
    if (isProcessingPayment) {
      return (
        <section className="section dashboard animate-fade-in">
          <div className="container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <div className="status-screen-card pending-review" style={{ textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto 1.5rem auto' }}></div>
              <h2 style={{ color: 'var(--color-primary)' }}>Initiating Payment...</h2>
              <p style={{ color: '#fff', opacity: 0.8 }}>Please do not close or refresh this page. You will be redirected to the payment gateway securely.</p>
            </div>
          </div>
        </section>
      );
    }

    if (currentPage === 'policies') {
      return <Policies onBack={() => setCurrentPage(currentUser ? 'dashboard' : 'home')} activeSection={policySection} />;
    }

    if (!currentUser) {
      if (currentPage === 'dashboard' || currentPage === 'events' || currentPage === 'history') {
        return (
          <div className="loading-screen" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--color-primary)' }}>
            <h3>Preparing your dashboard...</h3>
          </div>
        );
      }

      switch (currentPage) {
        case 'login':
          return <LoginForm onLogin={handleLoginSuccess} onSwitch={() => setCurrentPage('register')} registrationEnabled={registrationEnabled} />;
        case 'register':
          return (
            <RegistrationForm
              onSuccess={handleRegisterSuccess}
              onSwitch={() => setCurrentPage('login')}
              prices={prices}
              eventNames={eventNames}
              eventVisibility={eventVisibility}
              tournamentYear={tournamentYear}
              registrationEnabled={registrationEnabled}
              setIsProcessingPayment={setIsProcessingPayment}
            />
          );
        default:
          return (
            <>
              <Hero
                onRegister={() => {
                  if (paraEventOpen) {
                    window.location.href = '/para';
                  } else {
                    setCurrentPage('register');
                  }
                }}
                bgImage={homeBgImage}
                title={tournamentTitle}
                year={tournamentYear}
                dates={tournamentDates}
                venue={tournamentVenue}
                lastDateEntry={lastDateEntry}
                lastDateWithdrawal={lastDateWithdrawal}
                description={heroDescription}
                registrationEnabled={paraEventOpen ? true : registrationEnabled}
                registerButtonText={paraEventOpen ? "Register for Para Event" : undefined}
              />
              <TournamentRules prices={prices} eventNames={eventNames} eventVisibility={eventVisibility} year={tournamentYear} />
              <About />
            </>
          );
      }
    }

    // Role-based routing
    if (currentPage === 'home') {
      return (
        <>
          <Hero
            onRegister={() => {
              if (paraEventOpen) {
                window.location.href = '/para';
              } else {
                setCurrentPage('register');
              }
            }}
            bgImage={homeBgImage}
            title={tournamentTitle}
            year={tournamentYear}
            dates={tournamentDates}
            venue={tournamentVenue}
            lastDateEntry={lastDateEntry}
            lastDateWithdrawal={lastDateWithdrawal}
            description={heroDescription}
            registrationEnabled={paraEventOpen ? true : registrationEnabled}
            registerButtonText={paraEventOpen ? "Register for Para Event" : undefined}
            user={currentUser}
            onDashboard={() => setCurrentPage('dashboard')}
          />
          <TournamentRules prices={prices} eventNames={eventNames} eventVisibility={eventVisibility} />
          <About />
        </>
      );
    }

    if (currentUser.role === 'admin') {
      // Global loading guard for admin pages to prevent crashes
      if (users === null) {
        return (
          <div className="loading-spinner-container">
            <div className="spinner"></div>
            <p style={{ marginTop: '1rem', color: 'var(--color-primary)' }}>Synchronizing database...</p>
          </div>
        );
      }

      switch (currentPage) {
        case 'dashboard':
          return <AdminDashboard
            users={users}
            resetRequests={resetRequests}
            tournamentInfo={{
              title: tournamentTitle,
              year: tournamentYear,
              dates: tournamentDates,
              venue: tournamentVenue,
              lastDateEntry: lastDateEntry,
              lastDateWithdrawal: lastDateWithdrawal,
              description: heroDescription,
              registrationEnabled: registrationEnabled
            }}
            onNavigate={setCurrentPage}
            homeBgImage={homeBgImage}
          />;
        case 'requests':
          return <RequestsPage users={users} resetRequests={resetRequests} onBack={() => setCurrentPage('dashboard')} eventNames={eventNames} />;
        case 'history':
          return (
            <HistoryPage
              user={currentUser}
              allUsers={users}
              onBack={() => setCurrentPage('dashboard')}
              prices={prices}
              eventNames={eventNames}
              tournamentInfo={{ title: tournamentTitle, year: tournamentYear }}
            />
          );
        case 'report':
          return <ReportPage allUsers={users} tournamentInfo={{ title: tournamentTitle, year: tournamentYear }} onBack={() => setCurrentPage('dashboard')} eventNames={eventNames} prices={prices} eventVisibility={eventVisibility} />;
        case 'players':
          return <PlayersPage allUsers={users} tournamentInfo={{ title: tournamentTitle, year: tournamentYear }} eventNames={eventNames} prices={prices} eventVisibility={eventVisibility} />;
        case 'fees':
          return <ManageFees user={currentUser} onBack={() => setCurrentPage('dashboard')} onUpdatePrices={setPrices} prices={prices} eventNames={eventNames} eventVisibility={eventVisibility} onUpdateVisibility={setEventVisibility} />;
        default:
          return <AdminDashboard
            users={users}
            resetRequests={resetRequests}
            tournamentInfo={{
              title: tournamentTitle,
              year: tournamentYear,
              dates: tournamentDates,
              venue: tournamentVenue,
              lastDateEntry: lastDateEntry,
              lastDateWithdrawal: lastDateWithdrawal,
              description: heroDescription,
              registrationEnabled: registrationEnabled
            }}
            onNavigate={setCurrentPage}
            homeBgImage={homeBgImage}
          />;
      }
    }

    // Player Views
    if (currentPage === 'history') {
      return (
        <HistoryPage
          user={currentUser}
          onBack={() => setCurrentPage('dashboard')}
          prices={prices}
          tournamentInfo={{ title: tournamentTitle, year: tournamentYear }}
        />
      );
    }

    if (currentUser.status === 'pending' && (currentUser.transactionId || currentUser.selectedEvents?.length > 0)) {
      return (
        <section className="section dashboard animate-fade-in">
          <div className="container">
            <div className="status-screen-card pending-review">
              <div className="status-icon pulse-yellow">⌛</div>
              <h2>Registration Pending</h2>
              <p>Thank you for registering, <strong>{currentUser.name}</strong>!</p>
              <div className="status-msg-box">
                Your registration (ID: {currentUser.userId}) is currently being reviewed by our administrative team.
                Once your payment/exemption is verified, your dashboard will be fully activated.
              </div>
              <p className="check-back-hint">Please check back in a few days. You will be able to access all features once approved.</p>
              <button className="btn-header-login" style={{ marginTop: '2rem' }} onClick={() => { auth.signOut(); setCurrentPage('home'); }}>LOGOUT & EXIT</button>
            </div>
          </div>
        </section>
      );
    }

    if (currentUser.status === 'refunded') {
      return (
        <section className="section dashboard animate-fade-in">
          <div className="container">
            <div className="status-screen-card rejected" style={{ borderColor: '#607d8b' }}>
              <div className="status-icon" style={{ background: '#607d8b' }}>↺</div>
              <h2 style={{ color: '#607d8b' }}>Registration Refunded</h2>
              <p>Your tournament registration has been marked as <strong>REFUNDED</strong>.</p>
              <div className="status-msg-box">
                The fees paid for your registration have been processed for a refund. You are no longer registered for this tournament.
              </div>
              <p className="contact-support">For details regarding the refund timeline, please contact the organizers.</p>
              <button className="btn-header-login" style={{ marginTop: '2rem' }} onClick={() => { auth.signOut(); setCurrentPage('home'); }}>LOGOUT & EXIT</button>
            </div>
          </div>
        </section>
      );
    }

    if (currentUser.status === 'rejected' && (!currentUser.selectedEvents || currentUser.selectedEvents.length === 0)) {
      return (
        <section className="section dashboard animate-fade-in">
          <div className="container">
            <div className="status-screen-card rejected">
              <div className="status-icon">✗</div>
              <h2>Registration Rejected</h2>
              <p>We're sorry, your registration for the following event(s) has been rejected:</p>

              <div className="rejected-events-list-player" style={{
                margin: '1.5rem 0',
                padding: '1rem',
                background: 'rgba(239, 68, 68, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(239, 68, 68, 0.1)',
                textAlign: 'left'
              }}>
                {(currentUser.selectedEvents || []).map(eid => (
                  <div key={eid} style={{ color: '#ef4444', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>•</span>
                    <span>{eventNames?.[eid] || eid}</span>
                  </div>
                ))}
              </div>

              <p>Reason provided by admin:</p>
              <div className="reject-reason-box">
                {currentUser.rejectReason || "No reason provided. Please contact support."}
              </div>
              <p className="contact-support">If you have any questions, please contact the tournament organizers at <strong>ttab-support@baroda.com</strong></p>
              <button className="btn-header-login" onClick={() => { auth.signOut(); setCurrentPage('home'); }}>Logout</button>
            </div>
          </div>
        </section>
      );
    }

    return (
      <Dashboard
        user={currentUser}
        onBack={() => setCurrentPage('home')}
        onNavigate={setCurrentPage}
        onUpdateUser={handleUpdateUser}
        prices={prices}
        eventNames={eventNames}
        eventVisibility={eventVisibility}
        tournamentYear={tournamentYear}
        tournamentTitle={tournamentTitle}
        tournamentDates={tournamentDates}
        registrationEnabled={registrationEnabled}
      />
    );
  };

  // Dedicated Route for Para Tournament (/para)
  if (isPara) {
    return (
      <Suspense fallback={
        <div className="loading-spinner-container">
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: 'var(--color-primary)' }}>Loading Para Tournament...</p>
        </div>
      }>
        <ParaApp />
      </Suspense>
    );
  }

  if (loading || !eventVisibilityLoaded) {
    return (
      <div className="loading-spinner-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className={`App ${scrolled ? 'header-scrolled' : ''}`}>
      <Header
        user={currentUser}
        currentPage={currentPage}
        onNavigate={(page) => {
          if (page === 'register' && paraEventOpen) {
            window.location.href = '/para';
          } else {
            setCurrentPage(page);
          }
        }}
        onLogout={handleLogout}
        registrationEnabled={paraEventOpen ? true : registrationEnabled}
        pendingCount={users ? users.filter(u => u.status === 'pending').length : 0}
        isProcessingPayment={isProcessingPayment}
      />
      <main>
        <Suspense fallback={
          <div className="loading-spinner-container">
            <div className="spinner"></div>
            <p style={{ marginTop: '1rem', color: 'var(--color-primary)' }}>Loading page...</p>
          </div>
        }>
          {renderContent()}
        </Suspense>
      </main>
      <footer className="site-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <h2>The Table Tennis Association of Baroda</h2>
              <div className="social-icons" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <a
                  href="https://www.instagram.com/ttab_baroda/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(0, 242, 254, 0.1)', transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.color = '#000'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0, 242, 254, 0.1)'; e.currentTarget.style.color = 'var(--color-primary)'; }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                </a>
                <a
                  href="https://www.facebook.com/tabletennisassociationbaroda/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(0, 242, 254, 0.1)', transition: 'all 0.3s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.color = '#000'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0, 242, 254, 0.1)'; e.currentTarget.style.color = 'var(--color-primary)'; }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                </a>
              </div>
            </div>
            <div className="footer-contact">
              <h3 style={{ color: 'var(--color-primary)' }}>For any query contact</h3>
              <p>Chirag Golaniya (+91 94272 63831)</p>
              <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-primary)', opacity: 0.8 }}>
                <p>Email: kalpesh.ttab@gmail.com</p>
                <p style={{ marginTop: '0.5rem' }}><strong>Playing Address:</strong> TTAB-MSU TT Centre, Union Pavilion, The Maharaja Sayajirao University of Baroda, Fatehgunj, Vadodara.</p>
                <p style={{ marginTop: '0.5rem' }}><strong>Registered Address:</strong> 40, Aditya Bungalow, Nr Keya Motors, Chhani Jakatnaka, Vadodara, Gujarat, 390002</p>
              </div>
            </div>
            <div className="footer-links">
              <h3 style={{ color: 'var(--color-primary)', marginBottom: '1rem' }}>Legal</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, opacity: 0.8, lineHeight: '2' }}>
                <li><button onClick={() => { setPolicySection('terms'); setCurrentPage('policies'); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'left' }}>Terms & Conditions</button></li>
                <li><button onClick={() => { setPolicySection('privacy'); setCurrentPage('policies'); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'left' }}>Privacy Policy</button></li>
                <li><button onClick={() => { setPolicySection('refund'); setCurrentPage('policies'); }} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontFamily: 'inherit', fontSize: 'inherit', textAlign: 'left' }}>Refund & Cancellation Policy</button></li>
              </ul>
            </div>
          </div>
          <div style={{ marginTop: '4rem', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
            <p>© {tournamentYear} Table Tennis Association of Baroda. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;